/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import ScreenFrameBuffer from "App/render/webgl/utils/screen_frame_buffer";
import {Framebuffer} from "App/utils/frame_buffer";
import MemoryManagement from "App/memory_management";
import {logWarning} from "App/debug";
import {vec2} from "gl-matrix";
import WebGLContext from "App/web_gl_context";

export default
class OffscreenFrameBuffer extends ScreenFrameBuffer {
    private renderBufferID: WebGLRenderbuffer;
    private colorTextureID: WebGLTexture;
    private depthTextureID: WebGLTexture;

    constructor(aFramebufferUtil: Framebuffer) {
        super(aFramebufferUtil);

        this.renderBufferID = null;
        this.colorTextureID = null;
        this.depthTextureID = null;
    }
    
    public getColorTexture(): WebGLTexture {
        return this.colorTextureID;
    }

    public getDepthTexture(): WebGLTexture {
        return this.depthTextureID;
    }

    public initialize() {
        let gl = this._context.getWebGLRenderingContext();
        let params: any = this.framebuffer.getParameters();

        this.fboID = gl.createFramebuffer();

        if (params.createColorTexture) {
            this.colorTextureID = this.initTex(params.size, params.colorTextureFiltering, params.colorTexturePixelDataType, params.mipmap);
        }

        if (params.createDepthTexture) {
            this.depthTextureID = this.initTex(params.size, params.depthTextureFiltering, params.depthTexturePixelDataType, false);
        }

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboID);

        if (params.createDepthRenderBuffer) {
            this.renderBufferID = gl.createRenderbuffer();
            gl.bindRenderbuffer(gl.RENDERBUFFER, this.renderBufferID);
            gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, params.size[0], params.size[1]);
            gl.bindRenderbuffer(gl.RENDERBUFFER, null);

            MemoryManagement.addRenderBufferMem(params.size[0], params.size[1], 3);
        }

        // attach buffers to fbo
        if (params.createColorTexture) {
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.colorTextureID, 0);
        }

        if (params.createDepthTexture) {
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, this.depthTextureID, 0);
        } else if (params.createDepthRenderBuffer) {
            gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.renderBufferID);
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        let status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status !== gl.FRAMEBUFFER_COMPLETE) {
            logWarning("[Context|InitFBO] FBO-Status: " + status);
        }
    };

    public release() {
        super.release();

        let params = this.framebuffer.getParameters();
        let gl = this._context.getWebGLRenderingContext();

        if (this.fboID) {
            gl.deleteFramebuffer(this.fboID);
        }

        if (this.renderBufferID) {
            gl.deleteRenderbuffer(this.renderBufferID);
            MemoryManagement.removeRenderBufferMem(params.size[0], params.size[1], 3);
        }

        if (this.colorTextureID) {
            gl.deleteTexture(this.colorTextureID);
            MemoryManagement.removeRenderBufferMem(params.size[0], params.size[1], 4);
        }

        if (this.depthTextureID) {
            gl.deleteTexture(this.depthTextureID);
            MemoryManagement.removeRenderBufferMem(params.size[0], params.size[1], 4);
        }

        this.renderBufferID = null;
        this.colorTextureID = null;
        this.depthTextureID = null;
    }

    /**
     * Set parameters for texture
     * @param {number[]} aSize width / height
     * @param {gl.LINEAR, gl.NEAREST} aFiltering
     * @param {gl.FLOAT, gl.BYTE} aPixelDataType
     * @param {boolean} [aUseMipmap=false] optional
     * @return {WebGLTexture}
     */
    public initTex(aSize: vec2, aFiltering: number, aPixelDataType: number, aUseMipmap) {
        /** @type WebGLRenderingContext */
        let gl = this._context.getWebGLRenderingContext();
        let texID = gl.createTexture();
        let mipmap = aUseMipmap !== undefined && aUseMipmap;

        gl.bindTexture(gl.TEXTURE_2D, texID);

        // Use mipmap for min filter if set
        if (mipmap) {
            if (aFiltering === gl.NEAREST) {
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST);
            } else {
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
            }
        } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, aFiltering);
        }

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, aFiltering);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        // @ts-ignore
        if (WebGLContext.isWebGL2 && aPixelDataType === gl.FLOAT) {
            // @ts-ignore
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, aSize[0], aSize[1], 0, gl.RGBA, aPixelDataType, null);
        }
        // @ts-ignore
        else if (WebGLContext.isWebGL2 && aPixelDataType === gl.HALF_FLOAT) {
            // @ts-ignore
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, aSize[0], aSize[1], 0, gl.RGBA, aPixelDataType, null);
        }
        else {
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, aSize[0], aSize[1], 0, gl.RGBA, aPixelDataType, null);
        }

        // Generate mipmaps if asked for
        if (mipmap) {
            gl.generateMipmap(gl.TEXTURE_2D);
        }

        gl.bindTexture(gl.TEXTURE_2D, null);
        MemoryManagement.addRenderBufferMem(aSize[0], aSize[1], 4);

        return texID;
    }
}
